import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Space,
  Row,
  Col,
  Badge,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Option } = Select;

// An entry is matched against the build number and platform an app reports, so
// the browser and TV channels are never offered here.
const EXCLUDED_SALES_CHANNELS = ["WEB", "TV"];

const AddMobileAppBuildConfig = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  const [loading, setLoading] = useState(false);
  const [salesChannels, setSalesChannels] = useState([]);

  // The channel dropdown lists every configured sales channel; leaving it empty
  // makes the entry apply to all of them.
  const fetchSalesChannels = useCallback(async () => {
    try {
      const response = await masterService.getSalesChannels({
        perPage: 100,
        currentPage: 1,
      });

      if (response.data && response.data.status) {
        const channelsData = response.data.data?.data || response.data.data || [];
        setSalesChannels(
          (Array.isArray(channelsData) ? channelsData : []).filter(
            (channel) =>
              !EXCLUDED_SALES_CHANNELS.includes(
                String(channel.sc_name ?? "").trim().toUpperCase()
              )
          )
        );
      }
    } catch (error) {
      console.error("Error fetching sales channels:", error);
      setSalesChannels([]);
    }
  }, []);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await masterService.addMobileAppBuildConfig(values);

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Build config added successfully",
        });
        form.resetFields();
        navigate("/mobile-app-build-config");
      } else {
        notify.error({
          message: response.data.message || "Failed to add build config",
        });
      }
    } catch (error) {
      console.error("Error adding build config:", error);
      notify.error({
        message: error?.data?.message || "Error adding build config",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/mobile-app-build-config");
  };

  useEffect(() => {
    setPageTitle("Add Mobile App Build Config");
    fetchSalesChannels();
  }, [fetchSalesChannels, setPageTitle]);

  return (
    <div className="min-h-screen">
      <Card>
        <Form
          form={form}
          layout="vertical"
          size="large"
          onFinish={handleSubmit}
          initialValues={{
            // An entry exists to restrict a build - an enabled one matches the
            // default every unlisted build already gets.
            payment_gateway_enabled: false,
            is_active: "Y",
          }}
          className="space-y-6"
        >
          {/* Build Config Information Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <AppstoreOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Build Config Information
              </Title>
            </div>

            <Text
              type="secondary"
              style={{ display: "block", marginBottom: "16px" }}
            >
              The build number must match what the app sends in its
              <code> x-build-number </code> header. Builds with no entry here
              keep the payment gateway enabled.
            </Text>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Build Number
                    </span>
                  }
                  name="build_number"
                  rules={[
                    { required: true, message: "Please enter build number" },
                  ]}
                >
                  <Input
                    placeholder="Enter build number"
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Sales Channel
                    </span>
                  }
                  name="sc_id"
                  rules={[
                    { required: true, message: "Please select a sales channel" },
                  ]}
                >
                  <Select
                    className="rounded-lg"
                    placeholder="Select sales channel"
                  >
                    {salesChannels.map((channel) => (
                      <Option key={channel.sc_id} value={channel.sc_id}>
                        {channel.sc_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Payment Gateway
                    </span>
                  }
                  name="payment_gateway_enabled"
                >
                  <Select className="rounded-lg">
                    <Option value={true}>
                      <Badge status="success" text="Enabled" />
                    </Option>
                    <Option value={false}>
                      <Badge status="error" text="Disabled" />
                    </Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Status
                    </span>
                  }
                  name="is_active"
                >
                  <Select className="rounded-lg">
                    <Option value="Y">
                      <Badge status="success" text="Active" />
                    </Option>
                    <Option value="N">
                      <Badge status="default" text="Inactive" />
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Action Buttons */}
          <div
            className="flex justify-end gap-4 pt-6 border-t"
            style={{ borderColor: "#d9d9d9" }}
          >
            <Space size="middle">
              <Button
                onClick={handleReset}
                icon={<ReloadOutlined />}
                size="large"
                className="px-6"
              >
                Reset
              </Button>
              <Button
                onClick={handleGoBack}
                icon={<ArrowLeftOutlined />}
                size="large"
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                size="large"
                className="px-8"
              >
                {loading ? "Adding..." : "Add Build Config"}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default AddMobileAppBuildConfig;
